/**
 * Legal Section Component
 * Numbered section for legal documents with consistent styling
 */

import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { MdCheck } from 'react-icons/md';

/**
 * Legal Section with optional numbering
 */
export const LegalSection = ({ number, title, children, id }) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const numberColor = useColorModeValue('brand.600', 'brand.300');

  return (
    <Box as="section" id={id} scrollMarginTop="100px">
      <HStack align="baseline" spacing={3} mb={4}>
        {number && (
          <Text fontSize="lg" fontWeight="bold" color={numberColor} minW="30px">
            {number}.
          </Text>
        )}
        <Heading as="h2" size="md" color={headingColor}>
          {title}
        </Heading>
      </HStack>
      <Box pl={number ? { base: 0, md: 10 } : 0}>
        <VStack align="stretch" spacing={4}>
          {children}
        </VStack>
      </Box>
    </Box>
  );
};

/**
 * Legal Paragraph with proper styling
 */
export const LegalParagraph = ({ children }) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Text color={textColor} lineHeight="tall">
      {children}
    </Text>
  );
};

/**
 * Legal Bullet List
 */
export const LegalList = ({
  items,
  icon = MdCheck,
  iconColor = 'green.500',
}) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <List spacing={2}>
      {items.map((item, index) => (
        <ListItem key={index} display="flex" alignItems="flex-start">
          <ListIcon as={icon} color={iconColor} mt={1} />
          <Text color={textColor}>{item}</Text>
        </ListItem>
      ))}
    </List>
  );
};

/**
 * Legal Warning/Note Box
 */
export const LegalDisclaimer = ({
  type = 'info', // info, warning, error
  title,
  children,
}) => {
  const statusMap = {
    info: 'info',
    warning: 'warning',
    error: 'error',
  };

  return (
    <Alert
      status={statusMap[type]}
      variant="subtle"
      flexDirection="column"
      alignItems="flex-start"
      borderRadius="md"
      py={4}
    >
      {title && (
        <HStack mb={2}>
          <AlertIcon />
          <AlertTitle>{title}</AlertTitle>
        </HStack>
      )}
      <AlertDescription w="100%">{children}</AlertDescription>
    </Alert>
  );
};

/**
 * Legal Table for structured data
 */
export const LegalTable = ({ headers, rows }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr bg={headerBg}>
            {headers.map((header, index) => (
              <Th
                key={index}
                borderColor={borderColor}
                color={textColor}
                fontWeight="semibold"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, rowIndex) => (
            <Tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <Td key={cellIndex} borderColor={borderColor} color={textColor}>
                  {cell}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

/**
 * Legal Sub-section (nested under main section)
 */
export const LegalSubSection = ({ title, children }) => {
  const headingColor = useColorModeValue('gray.700', 'gray.100');

  return (
    <Box mt={4}>
      <Heading as="h3" size="sm" color={headingColor} mb={2}>
        {title}
      </Heading>
      <VStack align="stretch" spacing={2} pl={4}>
        {children}
      </VStack>
    </Box>
  );
};

/**
 * Legal Contact Info
 */
export const LegalContact = ({ label, email, description }) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const labelColor = useColorModeValue('gray.800', 'white');

  return (
    <Box p={4} borderRadius="md" bg={useColorModeValue('gray.50', 'gray.700')}>
      <Text fontWeight="semibold" color={labelColor} mb={1}>
        {label}
      </Text>
      <Text
        as="a"
        href={`mailto:${email}`}
        color="brand.500"
        textDecoration="underline"
      >
        {email}
      </Text>
      {description && (
        <Text fontSize="sm" color={textColor} mt={1}>
          {description}
        </Text>
      )}
    </Box>
  );
};

/**
 * Section Divider
 */
export const LegalDivider = () => {
  return <Divider my={6} />;
};

const legalComponents = {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalTable,
  LegalSubSection,
  LegalContact,
  LegalDivider,
};

export default legalComponents;
